/**
 * PageEditorDialogs — Alert dialogs for delete confirmation and publish confirmation.
 * Extracted from PageEditorPage.tsx (Plan 150, Epic 03).
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { EditorSection } from './usePageSections'
import { countDescendants } from './page-editor-helpers'

interface DeleteSectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  targetSection: EditorSection | null
}

export function DeleteSectionDialog({ open, onOpenChange, onConfirm, targetSection }: DeleteSectionDialogProps) {
  const desc = targetSection ? countDescendants(targetSection) : 0

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {desc > 0 ? (
            <>
              <AlertDialogTitle>
                Delete this container and all {desc} section{desc > 1 ? 's' : ''} inside it?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This container and all sections inside it will be removed. This action cannot be undone.
              </AlertDialogDescription>
            </>
          ) : (
            <>
              <AlertDialogTitle>Delete this section?</AlertDialogTitle>
              <AlertDialogDescription>
                This section and its content will be removed from the page. This action cannot be undone.
              </AlertDialogDescription>
            </>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-500 text-white hover:bg-red-600"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface PublishDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isPublishing: boolean
  isCardEditor: boolean
}

export function PublishDialog({ open, onOpenChange, onConfirm, isPublishing, isCardEditor }: PublishDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isCardEditor ? 'Apply card design to storefront?' : 'Publish changes?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isCardEditor
              ? 'All product cards in grids, carousels, and collections will update.'
              : 'Your storefront will be updated immediately. Customers will see the new design right away.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isPublishing}
            className="bg-sky-500 text-white hover:bg-sky-600"
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
